import React, { SyntheticEvent, useCallback, useMemo, useState } from 'react';
import { Accordion, AccordionDetails, AccordionSummary, Grid, Typography } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { DeleteForever } from '@mui/icons-material';
import { SingleRegistrationForm } from './SingleRegistrationForm';
import {
  CreateEventRegistration,
  CreateEventRegistrationFields,
  EventRegistrationForm,
} from '../generated/api/dsv-public/model';
import { useTranslation } from 'react-i18next';
import { PricingSummary } from './form.types';
import { SingleFormProvider } from '../context/SingleForm';

type ToggleableSingleRegistrationFormProps = {
  registrationForm: EventRegistrationForm;
  prefilledFields: CreateEventRegistrationFields;
  prefilledOptionValues: number[];
  handleDeleteForm: (formIndex: number) => void;
  handleUpdateForm: (formIndex: number, registrationValues: CreateEventRegistration) => void;
  handleUpdatePricingSummary: (formIndex: number, pricingSummary: PricingSummary[]) => void;
  isDeletable: boolean;
  formIndex: number;
};

export const ToggleableSingleRegistrationForm: React.FC<ToggleableSingleRegistrationFormProps> = ({
  registrationForm,
  prefilledFields,
  prefilledOptionValues,
  handleDeleteForm,
  handleUpdateForm,
  handleUpdatePricingSummary,
  isDeletable,
  formIndex,
}) => {
  const [isOpen, setIsOpen] = useState<boolean>(true);
  const [firstName, setFirstName] = useState<string>('');
  const [lastName, setLastName] = useState<string>('');
  const { t } = useTranslation();

  const handleToggleSingleForm = useCallback((event: SyntheticEvent<Element, any>) => {
    if (
      // @ts-ignore Special Target: Delete Icon
      event.target?.dataset?.testid === 'DeleteForeverIcon' ||
      // @ts-ignore Special Target: Delete Icon path
      event.target?.parentNode.dataset?.testid === 'DeleteForeverIcon'
    ) {
      return;
    }
    setIsOpen(prev => !prev);
  }, []);

  const handleDelete = useCallback(() => {
    handleDeleteForm(formIndex);
  }, [handleDeleteForm, formIndex]);

  const handleUpdate = useCallback(
    (registrationValues: CreateEventRegistration) => {
      if ((registrationValues.fields['firstName'] as any) !== firstName) {
        setFirstName(registrationValues.fields['firstName'] as any as string);
      }
      if ((registrationValues.fields['lastName'] as any) !== lastName) {
        setLastName(registrationValues.fields['lastName'] as any as string);
      }
      handleUpdateForm(formIndex, registrationValues);
    },
    [firstName, lastName, handleUpdateForm, formIndex]
  );

  const title = useMemo(() => {
    if (firstName && firstName.length > 0 && (!lastName || lastName.length === 0)) {
      return firstName;
    }
    if (lastName && lastName.length > 0 && (!firstName || firstName.length === 0)) {
      return lastName;
    }
    if (firstName && firstName.length > 0 && lastName && lastName.length > 0) {
      return `${lastName}, ${firstName}`;
    }
    return t('form.toggleable-registration.new-participant');
  }, [firstName, lastName, t]);

  return (
    <Accordion expanded={isOpen} onChange={handleToggleSingleForm}>
      <AccordionSummary sx={{ flexDirection: 'row-reverse' }} expandIcon={<ExpandMoreIcon />}>
        <Grid sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
          <Typography>{title}</Typography>
          {isDeletable && <DeleteForever onClick={handleDelete} color="error" />}
        </Grid>
      </AccordionSummary>
      <AccordionDetails>
        <SingleFormProvider formIndex={formIndex}>
          <SingleRegistrationForm
            isSingularRegistration={false}
            prefilledFields={prefilledFields}
            prefilledOptionValues={prefilledOptionValues}
            registrationForm={registrationForm}
            handleSubmitForm={handleUpdate}
            handleUpdatePricingSummary={pricingSummary => handleUpdatePricingSummary(formIndex, pricingSummary)}
          />
        </SingleFormProvider>
      </AccordionDetails>
    </Accordion>
  );
};
